'use client';

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

type FooterAccordionValue = {
    openId: string | null;
    toggle: (id: string) => void;
};

const FooterAccordionContext = createContext<FooterAccordionValue | null>(null);

/** The box around the footer sections; keeps one open at a time, so opening one folds the other. */
export function FooterAccordion({ className, children }: { className?: string; children: ReactNode }) {
    const [openId, setOpenId] = useState<string | null>(null);
    const value = useMemo<FooterAccordionValue>(
        () => ({ openId, toggle: (id) => setOpenId((current) => (current === id ? null : id)) }),
        [openId],
    );

    return (
        <FooterAccordionContext.Provider value={value}>
            <div className={className}>{children}</div>
        </FooterAccordionContext.Provider>
    );
}

export function useFooterAccordion(id: string) {
    const ctx = useContext(FooterAccordionContext);
    if (!ctx) {
        throw new Error('useFooterAccordion must be used inside <FooterAccordion>');
    }

    return { isOpen: ctx.openId === id, toggle: () => ctx.toggle(id) };
}
